import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { CartProductService, OrderService, ProductService, ProductTypeValueService, UsersService, UserAddressService, VoucherService } from '@app/api/api';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';

// Interfaces
interface Customer {
  id: string;
  full_name: string;
  email?: string;
  phone_number?: string;
  [key: string]: any;
}

interface Product {
  id: string;
  name: string;
  product_code?: string;
  price?: number;
  origin_price?: number;
  default_image?: string;
  [key: string]: any;
}

interface CartItem {
  id: string;
  user_id: string;
  product_id: string;
  quantity: number;
  product_type_id?: string;
  extend_product?: Product;
  extend_product_price?: number;
  [key: string]: any;
}

interface Address {
  id: string;
  user_id: string;
  address?: string;
  province?: string;
  district?: string;
  ward?: string;
  [key: string]: any;
}

interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data?: T;
}

interface PaginatedData<T> {
  collection: T[];
  total?: number;
}

@Component({
  selector: 'meu-create-order-for-customer',
  templateUrl: './create-order-for-customer.component.html',
  styleUrls: ['./create-order-for-customer.component.scss']
})
export class CreateOrderForCustomerComponent implements OnInit {
  // Customer selection
  customerSearchControl = new FormControl();
  filteredCustomers!: Observable<Customer[]>;
  selectedCustomer: Customer | null = null;

  // Product selection
  productSearchControl = new FormControl();
  filteredProducts!: Observable<Product[]>;
  selectedProduct: Product | null = null;
  availableSubTypes: { id: string; name?: string; price?: number; origin_price?: number }[] = [];
  selectedSubTypeId: string | null = null;
  productQuantity: number = 1;

  // Cart
  customerCart: CartItem[] = [];
  cartTotal: number = 0;

  // Address
  customerAddresses: Address[] = [];
  selectedAddressId: string = '';

  // Order
  orderNote: string = '';
  voucherCode: string = '';
  isApplyVAT: boolean = false;
  vatPercentage: number = 0;
  voucherValue: number = 0;
  voucherInfo: any = null;
  voucherError: string = '';

  loading: boolean = false;
  displayedColumns: string[] = ['image', 'product', 'quantity', 'price', 'total', 'actions'];

  constructor(
    private cartProductService: CartProductService,
    private orderService: OrderService,
    private productService: ProductService,
    private productTypeValueService: ProductTypeValueService,
    private usersService: UsersService,
    private userAddressService: UserAddressService,
    private voucherService: VoucherService,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef,
    public dialog: MatDialog
  ) {}

  ngOnInit() {
    this.setupCustomerSearch();
    this.setupProductSearch();
    this.loadVatConfig();
  }

  setupCustomerSearch() {
    this.filteredCustomers = this.customerSearchControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(value => {
        if (typeof value === 'string' && value.length > 0) {
          return this.searchCustomers(value);
        }
        return of([]);
      })
    );
  }

  setupProductSearch() {
    this.filteredProducts = this.productSearchControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(value => {
        if (typeof value === 'string' && value.length > 0) {
          return this.searchProducts(value);
        }
        return of([]);
      })
    );
  }

  searchCustomers(searchText: string): Observable<Customer[]> {
    const filter = `(full_name|email|phone_number)@=${searchText}`;
    return new Observable(observer => {
      this.usersService.apiUsersGet(filter, '', 1, 10).subscribe(
        (res: ApiResponse<PaginatedData<Customer>>) => {
          if (res.success && res.data) {
            observer.next(res.data.collection || []);
          } else {
            observer.next([]);
          }
          observer.complete();
        },
        error => {
          observer.next([]);
          observer.complete();
        }
      );
    });
  }

  searchProducts(searchText: string): Observable<Product[]> {
    const q = (searchText || '').trim();
  const filter = `(name|sapatco_code|product_code|sku)@=${q}`;
    return new Observable(observer => {
      this.productTypeValueService.apiFullProductsGet(filter, '', 1, 50).subscribe(
        (res: ApiResponse<PaginatedData<any>>) => {
          if (res.success && res.data) {
            // fullProducts API returns items shaped as { product: {...}, type_values: [...] }
            // Flatten so the rest of the component (autocomplete display, subtype selection) keeps working
            const items: Product[] = (res.data.collection || []).map((item: any) => {
              if (item && item.product) {
                return {
                  ...item.product,
                  extend_product_type: item.type_values || []
                };
              }
              return item;
            });
            const s = q.toLowerCase();
            items.sort((a: Product, b: Product) => {
              const score = (item: Product) => {
                const code = (item.product_code || '').toLowerCase();
                const name = (item.name || '').toLowerCase();
                if (code === s && s.length > 0) return 0;
                if (code.includes(s) && s.length > 0) return 1;
                if (name.includes(s) && s.length > 0) return 2;
                return 3;
              };
              return score(a) - score(b);
            });
            observer.next(items.slice(0, 10));
          } else {
            observer.next([]);
          }
          observer.complete();
        },
        error => {
          observer.next([]);
          observer.complete();
        }
      );
    });
  }

  displayCustomer(customer: Customer | null): string {
    return customer ? `${customer.full_name} (${customer.email || customer.phone_number})` : '';
  }

  displayProduct(product: Product | null): string {
    // Show product name in input after selection
    return product && product.name ? product.name : '';
  }

  onCustomerSelected(customer: Customer): void {
    this.selectedCustomer = customer;
    this.loadCustomerCart();
    this.loadCustomerAddresses();
  }

  onProductSelected(event: Product): void {
    const product = event;
    // Set display name in search box without triggering search again
    this.productSearchControl.setValue(product, { emitEvent: false });

    // Fetch full product detail to get subtype list & correct pricing
    this.loading = true;
    this.productService.apiProductsIdGet(product.id).subscribe(
      (res: ApiResponse<Product>) => {
        if (res.success && res.data) {
          this.selectedProduct = res.data;
          this.availableSubTypes = res.data.extend_product_type || [];
        } else {
          this.selectedProduct = product;
          this.availableSubTypes = (product && product.extend_product_type) || [];
        }
        this.selectedSubTypeId = this.availableSubTypes.length > 0 ? this.availableSubTypes[0].id : null;
        this.loading = false;
        this.cdr.detectChanges();
      },
      () => {
        this.selectedProduct = product;
        this.availableSubTypes = (product && product.extend_product_type) || [];
        this.selectedSubTypeId = this.availableSubTypes.length > 0 ? this.availableSubTypes[0].id : null;
        this.loading = false;
        this.cdr.detectChanges();
      }
    );
  }

  loadCustomerCart(): void {
    if (!this.selectedCustomer) return;

    this.loading = true;
    this.cartProductService.apiUserCartProductsGet(this.selectedCustomer.id).subscribe(
      (res: ApiResponse<PaginatedData<CartItem>>) => {
        if (res.success && res.data) {
          // Backend trả về data.collection chứ không phải data trực tiếp
          this.customerCart = res.data.collection || [];
          this.calculateCartTotal();
        } else {
          this.customerCart = [];
        }
        this.loading = false;
        this.cdr.detectChanges();
      },
      error => {
        this.toastr.error('Không thể tải giỏ hàng');
        this.customerCart = [];
        this.loading = false;
      }
    );
  }

  loadCustomerAddresses(): void {
    if (!this.selectedCustomer) return;

    this.userAddressService.apiUserUserIDUserAddressesGet(this.selectedCustomer.id).subscribe(
      (res: ApiResponse<Address[]>) => {
        if (res.success && res.data) {
          // /api/user/{id}/userAddresses returns data as a direct array (NOT paginated { collection })
          this.customerAddresses = Array.isArray(res.data) ? res.data : [];
          if (this.customerAddresses.length > 0) {
            this.selectedAddressId = this.customerAddresses[0].id;
          }
        }
        this.cdr.detectChanges();
      },
      error => {
        console.error('Cannot load addresses', error);
      }
    );
  }

  addProductToCart(): void {
    if (!this.selectedCustomer) {
      this.toastr.warning('Vui lòng chọn khách hàng');
      return;
    }

    if (!this.selectedProduct) {
      this.toastr.warning('Vui lòng chọn sản phẩm');
      return;
    }

    if (this.productQuantity < 1) {
      this.toastr.warning('Số lượng phải lớn hơn 0');
      return;
    }

    this.loading = true;
    this.cartProductService.apiCartProductsAddForCustomerPost(
      this.selectedCustomer.id,
      this.selectedProduct.id,
      this.productQuantity,
      this.selectedSubTypeId || undefined
    ).subscribe(
      (res: ApiResponse<any>) => {
        if (res.success) {
          this.toastr.success('Đã thêm sản phẩm vào giỏ hàng');
          this.loadCustomerCart();
          this.productSearchControl.setValue('');
          this.selectedProduct = null;
          this.availableSubTypes = [];
          this.selectedSubTypeId = null;
          this.productQuantity = 1;
        } else {
          this.toastr.error(res.message || 'Không thể thêm sản phẩm');
        }
        this.loading = false;
      },
      error => {
        this.toastr.error('Lỗi khi thêm sản phẩm vào giỏ hàng');
        this.loading = false;
      }
    );
  }

  removeFromCart(cartItem: CartItem): void {
    if (!confirm('Bạn có chắc muốn xóa sản phẩm này khỏi giỏ hàng?')) {
      return;
    }

    this.cartProductService.apiMyCartProductsIdDelete(cartItem.id).subscribe(
      (res: ApiResponse<any>) => {
        this.toastr.success('Đã xóa sản phẩm khỏi giỏ hàng');
        this.loadCustomerCart();
      },
      error => {
        this.toastr.error('Không thể xóa sản phẩm');
      }
    );
  }

  updateCartQuantity(cartItem: CartItem): void {
    this.cartProductService.apiMyCartProductsIdPut(cartItem.id, cartItem).subscribe(
      (res: ApiResponse<any>) => {
        this.toastr.success('Đã cập nhật số lượng');
        this.loadCustomerCart();
      },
      error => {
        this.toastr.error('Không thể cập nhật số lượng');
      }
    );
  }

  calculateCartTotal(): void {
    this.cartTotal = this.customerCart.reduce((total, item) => {
      // Lấy giá từ extend_product object
      const extendProduct: Product = item.extend_product || {} as Product;
      const price = extendProduct.price || extendProduct.origin_price || item.extend_product_price || 0;
      const quantity = item.quantity || 0;
      return total + (price * quantity);
    }, 0);
  }

  loadVatConfig(): void {
    this.orderService.apiOrdersVatConfigGet().subscribe((res: any) => {
      if (res && res.success) {
        this.vatPercentage = res.data || 0;
      }
      this.cdr.detectChanges();
    }, () => {
      this.cdr.detectChanges();
    });
  }

  onToggleVat(): void {
    if (this.isApplyVAT && !this.vatPercentage) {
      this.loadVatConfig();
    }
    this.cdr.detectChanges();
  }

  isAppliedVoucher: boolean = false;

  applyVoucher(): void {
    const code = (this.voucherCode || '').trim();
    if (!code) {
      this.toastr.warning('Vui lòng nhập mã voucher');
      return;
    }
    this.voucherService.apiVouchersCodeGet(code).subscribe(
      (res: any) => {
        if (res && res.success && res.data) {
          if (res.data.is_used) {
            this.isAppliedVoucher = false;
            this.voucherValue = 0;
            this.voucherInfo = null;
            this.voucherError = 'Mã voucher đã được sử dụng';
            this.toastr.error('Mã voucher đã được sử dụng');
          } else if (res.data.expiry && new Date(res.data.expiry) < new Date()) {
            this.isAppliedVoucher = false;
            this.voucherValue = 0;
            this.voucherInfo = null;
            this.voucherError = 'Mã voucher đã hết hạn';
            this.toastr.error('Mã voucher đã hết hạn');
          } else {
            this.isAppliedVoucher = true;
            this.voucherInfo = res.data;
            this.voucherValue = res.data.value || 0;
            this.voucherError = '';
            this.toastr.success('Áp dụng mã giảm giá thành công');
          }
        } else {
          this.isAppliedVoucher = false;
          this.voucherValue = 0;
          this.voucherInfo = null;
          this.voucherError = (res && res.message) || 'Mã voucher không hợp lệ';
          this.toastr.error(this.voucherError);
        }
        this.cdr.detectChanges();
      },
      (err: any) => {
        this.isAppliedVoucher = false;
        this.voucherValue = 0;
        this.voucherInfo = null;
        this.voucherError = 'Mã voucher không hợp lệ';
        this.toastr.error('Mã voucher không hợp lệ');
        this.cdr.detectChanges();
      }
    );
  }

  unApplyVoucher(): void {
    this.isAppliedVoucher = false;
    this.voucherCode = '';
    this.voucherValue = 0;
    this.voucherInfo = null;
    this.voucherError = '';
    this.cdr.detectChanges();
  }

  getDiscountedTotal(): number {
    return Math.max(0, this.cartTotal - this.voucherValue);
  }

  getVatAmount(): number {
    if (!this.isApplyVAT) return 0;
    return Math.round((this.getDiscountedTotal() * this.vatPercentage) / 100);
  }

  getFinalTotal(): number {
    return this.getDiscountedTotal() + this.getVatAmount();
  }

  createOrder(): void {
    if (!this.selectedCustomer) {
      this.toastr.warning('Vui lòng chọn khách hàng');
      return;
    }

    if (!this.customerCart || this.customerCart.length === 0) {
      this.toastr.warning('Giỏ hàng trống');
      return;
    }

    if (!this.selectedAddressId) {
      this.toastr.warning('Vui lòng chọn địa chỉ giao hàng');
      return;
    }

    if (!confirm('Bạn có chắc muốn tạo đơn hàng cho khách hàng này?')) {
      return;
    }

    const orderProducts = this.customerCart.map(item => ({
      productId: item.product_id,
      productTypeId: item.product_type_id || null,
      quantity: item.quantity || 1,
      productTypeText: item.product_type_id ? 'string' : null
    }));

    this.loading = true;
    this.orderService.apiOrdersCreateForCustomerPost(
      this.selectedCustomer.id,
      this.selectedAddressId,
      this.voucherCode || undefined,
      this.orderNote || undefined,
      this.isApplyVAT,
      orderProducts
    ).subscribe(
      (res: ApiResponse<any>) => {
        if (res.success) {
          this.toastr.success('Đã tạo đơn hàng thành công!');
          this.resetForm();
        } else {
          this.toastr.error(res.message || 'Không thể tạo đơn hàng');
        }
        this.loading = false;
      },
      error => {
        this.toastr.error('Lỗi khi tạo đơn hàng');
        this.loading = false;
      }
    );
  }

  resetForm(): void {
    this.selectedCustomer = null;
    this.customerSearchControl.setValue('');
    this.customerCart = [];
    this.customerAddresses = [];
    this.selectedAddressId = '';
    this.orderNote = '';
    this.voucherCode = '';
    this.isApplyVAT = false;
    this.cartTotal = 0;
    this.productSearchControl.setValue('');
    this.selectedProduct = null;
    this.availableSubTypes = [];
    this.selectedSubTypeId = null;
    this.productQuantity = 1;
  }
}
